import React, {
  useCallback, useEffect, useMemo, useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { bool } from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';

import { langType, withLang } from '../../../i18n';
import { publicationStatusValues } from '../../../constants/types';
import {
  updateSurveyOptionsAction,
  updateSurveySchemaOptionsAction
} from '../../../actions';
import SelectedExtraSurveyPart from './selectedExtraSurveyPart';
import styles from './addExtraSurveyPart.module.css';
import { routes } from '../../../constants';
import { getSurveySchema } from '../../../api';
import { setDocumentTitle } from '../../../util';
import useAsyncQuery from '../../../hooks/useAsyncQuery';
import BrowseSurveyParts from '../../SurveySchemas/BrowseSurveyParts';

const AddExtraSurveyPart = ({ toSchema, translate: t }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isSurveyFlow = !toSchema;
  const surveySchemaId = useSelector((state) => state.survey.surveyOptions?.schemaId);
  const surveyType = useSelector((state) => state.survey.surveyOptions?.surveyType);

  const isSchemaFlow = !!toSchema;
  const schemaType = useSelector((state) => state.surveySchema.surveySchemaOptions?.type);

  const [selectedExtraSurveyPart, setSelectedExtraSurveyPart] = useState(null);

  useEffect(() => {
    setDocumentTitle(t('startSurvey.extraSurveyPart.add'));
  }, [t]);

  useEffect(() => {
    if (isSurveyFlow && !surveySchemaId) {
      navigate(routes.surveys.index, { replace: true });
    }
  }, [isSurveyFlow, surveySchemaId]);

  const fetchSchemaExtraPart = useCallback(() => (
    getSurveySchema(surveySchemaId).then((s) => s?.extraSurveyPart ?? null)
  ), [surveySchemaId]);

  const schemaExtraSurveyPart = useAsyncQuery(fetchSchemaExtraPart, {
    initialData: undefined,
    condition: isSurveyFlow && surveySchemaId
  });

  const onSave = useCallback(() => {
    const payload = { extraSurveyPart: selectedExtraSurveyPart };
    if (isSchemaFlow) {
      dispatch(updateSurveySchemaOptionsAction(payload));
    } else if (isSurveyFlow) {
      dispatch(updateSurveyOptionsAction(payload));
    }
  }, [isSchemaFlow, isSurveyFlow, selectedExtraSurveyPart]);

  const onSelectExtraSurveyPart = useCallback(
    (extraSurveyPart) => setSelectedExtraSurveyPart(extraSurveyPart),
    []
  );

  const onRemoveExtraSurveyPart = useCallback(() => setSelectedExtraSurveyPart(null), []);

  const selectedParts = useMemo(
    () => (selectedExtraSurveyPart ? [selectedExtraSurveyPart] : []),
    [selectedExtraSurveyPart]
  );

  const excludedParts = useMemo(() => {
    const base = [];
    if (schemaExtraSurveyPart) base.push(schemaExtraSurveyPart);
    return base;
  }, [schemaExtraSurveyPart]);

  const effectiveType = isSchemaFlow ? schemaType : surveyType;

  return (
    <div className="page-container">
      <h2 className={styles.heading}>{t('extraSurveyParts.title')}</h2>

      <SelectedExtraSurveyPart
        onRemoveSchemaPart={onRemoveExtraSurveyPart}
        schemaParts={selectedParts}
      />

      {(isSchemaFlow || schemaExtraSurveyPart !== undefined) && effectiveType && (
        <BrowseSurveyParts
          isExtraPartsView
          selectable
          selectLimitOne
          lockedType={effectiveType}
          defaultPublicationStatus={publicationStatusValues.PUBLISHED}
          onSelectPart={onSelectExtraSurveyPart}
          selectedParts={selectedParts}
          excludedParts={excludedParts}
        />
      )}

      <div className={styles.actions}>
        <Link className="button--action" to=".." data-cy="cancel-button">
          {t('common.cancel')}
        </Link>
        <Link
          className="button--action"
          to=".."
          onClick={onSave}
          data-cy="add-button"
        >
          {t('common.add')}
        </Link>
      </div>
    </div>
  );
};

AddExtraSurveyPart.propTypes = {
  toSchema: bool,
  ...langType
};

AddExtraSurveyPart.defaultProps = {
  toSchema: false
};

export default withLang(AddExtraSurveyPart);
